import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { LOCALES } from "../constants";
import { useAuth } from "../context/AuthContext";
import { FaSignOutAlt, FaBars, FaTimes } from "react-icons/fa";
import logo from "../assets/logo_crisp_transparent.png";

function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Check if running in native app
  const isNativeApp = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();

  // All hooks must be called before any early returns (React Rules of Hooks)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef();

  // Find the locale that matches current i18n language, default to en-CA
  const currentLocale = LOCALES.find(l => l.code === i18n.language) || LOCALES[1];
  const [selected, setSelected] = useState(currentLocale);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      if (width >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }
    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  // Sync selected locale when i18n language changes
  useEffect(() => {
    const matchingLocale = LOCALES.find(l => l.code === i18n.language);
    if (matchingLocale && matchingLocale.code !== selected.code) {
      setSelected(matchingLocale);
    }
  }, [i18n.language, selected.code]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Hide header on standalone landing pages
  if (location.pathname.startsWith('/garage/')) {
    return null;
  }


  // Simplified header for native app
  if (isNativeApp) {
    const handleNativeLogout = async () => {
      try {
        await logout();
      } catch (error) {
        console.error("Logout error:", error);
      }
      navigate("/login");
    };

    return (
      <header className="site-header" style={{ justifyContent: 'space-between' }}>
        <Link to={isAuthenticated() ? "/native-dashboard" : "/login"} className="header-logo-link">
          <img src={logo} alt="CompareMechanic" className="header-logo" />
        </Link>
        {isAuthenticated() && (
          <button
            onClick={handleNativeLogout}
            className="btn-header-logout"
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
            }}
          >
            <FaSignOutAlt /> {t("common.logout", "Logout")}
          </button>
        )}
      </header>
    );
  }

  const handleLogout = async () => {
    try {
      // Await logout completion before navigating
      // This ensures all cleanup (subscriptions, storage, etc.) is done first
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Force full page reload to ensure all React state is completely cleared
    // This is more reliable than React Router navigation for logout
    window.location.href = "/";
  };

  // Render country selector (reusable for both mobile and desktop)
  const renderCountrySelector = (inDropdown = false) => (
    <div className={`country-selector ${inDropdown ? 'in-dropdown' : ''}`} ref={!inDropdown ? dropdownRef : undefined}>
      <button
        className="country-selector-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ReactCountryFlag
          countryCode={selected.countryCode}
          svg
          className="country-flag"
          title={selected.countryCode}
          aria-label={selected.label}
          alt=""
        />
        {selected.label}
        <svg className="country-dropdown-arrow" viewBox="0 0 20 20">
          <path
            d="M5.5 8l4.5 4.5L14.5 8"
            stroke={inDropdown ? "#333" : "#fff"}
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </button>

      {open && (
        <ul className="country-dropdown" role="listbox">
          {LOCALES.map((locale) => (
            <li key={locale.code} className="country-dropdown-item">
              <button
                onClick={() => {
                  setSelected(locale);
                  i18n.changeLanguage(locale.code);
                  setOpen(false);
                }}
                role="option"
                aria-selected={selected.code === locale.code}
              >
                <ReactCountryFlag
                  countryCode={locale.countryCode}
                  svg
                  className="country-flag-small"
                  title={locale.countryCode}
                  aria-label={locale.label}
                  alt=""
                />
                {locale.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <header className="site-header site-header-mobile-inline">
      {/* Logo - Left Side */}
      <Link to="/" className="header-logo-link">
        <img src={logo} alt="CompareMechanic" className="header-logo" />
      </Link>

      {/* Mobile Navigation - Now inline with logo */}
      {isMobile && (
        <div className="mobile-nav-container" ref={mobileMenuRef}>
          {/* Mobile Menu Toggle Button */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>

          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="mobile-menu-dropdown">
              {!isAuthenticated() ? (
                <>
                  <Link
                    to="/login"
                    className="mobile-menu-item"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("common.login")}
                  </Link>
                  <Link
                    to="/register"
                    className="mobile-menu-item mobile-menu-item-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("common.signUp")}
                  </Link>
                </>
              ) : (
                <>
                  {user?.type === "client" && (
                    <Link
                      to="/client-dashboard"
                      className="mobile-menu-item"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t("header.clientDashboard")}
                    </Link>
                  )}
                  {user?.type === "garage" && (
                    <Link
                      to="/garage-dashboard"
                      className="mobile-menu-item"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t("header.garageDashboard")}
                    </Link>
                  )}
                  {user?.type === "admin" && (
                    <Link
                      to="/admin/dashboard"
                      className="mobile-menu-item"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t("header.adminDashboard")}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="mobile-menu-item mobile-menu-item-logout"
                  >
                    <FaSignOutAlt /> {t("common.logout")}
                  </button>
                </>
              )}

              {/* Divider */}
              <div className="mobile-menu-divider" />

              {/* Language selector in mobile menu */}
              <div className="mobile-menu-section-label">{t("common.language") || "Language"}</div>
              {renderCountrySelector(true)}
            </div>
          )}
        </div>
      )}

      {/* Desktop Navigation */}
      {!isMobile && (
        <div className="header-nav">
          {!isAuthenticated() ? (
            <>
              <Link to="/login" style={{ textDecoration: "none" }}>
                <button className="btn-header-login">{t("common.login")}</button>
              </Link>

              <Link to="/register" style={{ textDecoration: "none" }}>
                <button className="btn-header-signup">{t("common.signUp")}</button>
              </Link>
            </>
          ) : (
            <>
              {user?.type === "client" && (
                <Link to="/client-dashboard" style={{ textDecoration: "none" }}>
                  <button className="btn-header-dashboard client-dashboard">
                    {t("header.clientDashboard")}
                  </button>
                </Link>
              )}
              {user?.type === "garage" && (
                <Link to="/garage-dashboard" style={{ textDecoration: "none" }}>
                  <button className="btn-header-dashboard garage-dashboard">
                    {t("header.garageDashboard")}
                  </button>
                </Link>
              )}
              {user?.type === "admin" && (
                <Link to="/admin/dashboard" style={{ textDecoration: "none" }}>
                  <button className="btn-header-dashboard admin-dashboard">
                    {t("header.adminDashboard")}
                  </button>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className={`btn-header-logout ${
                  user?.type === "client" ? "client-type" : user?.type === "admin" ? "admin-type" : "garage-type"
                }`}
              >
                <FaSignOutAlt /> {t("common.logout")}
              </button>
            </>
          )}

          {renderCountrySelector()}
        </div>
      )}
    </header>
  );
}

export default Header;
